import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Typography,
  Table,
  Button,
  Input,
  Tag,
  Divider,
  Spin,
  Modal,
  Space,
} from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import orderService from '../../services/orderService';
import ErrorMessage from '../common/ErrorMessage';

const { Title, Text } = Typography;

const OrderManager = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editedQuantity, setEditedQuantity] = useState('');
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const fetchOrder = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await orderService.getById(id);
      setOrder(response.order);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch order');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const handleEditItem = (item) => {
    setEditingItem(item.id);
    setEditedQuantity(item.quantity.toString());
  };

  const handleSaveItem = async () => {
    try {
      await orderService.update(
        id,
        order.orderItems.map((item) =>
          item.id === editingItem
            ? { ...item, quantity: parseInt(editedQuantity, 10) }
            : item
        )
      );
      setEditingItem(null);
      fetchOrder();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update item');
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditedQuantity('');
  };

  const handleConfirmOrder = async () => {
    try {
      await orderService.confirm(id);
      fetchOrder();
      setOpenConfirmDialog(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to confirm order');
    }
  };

  const handleCancelOrder = async () => {
    if (window.confirm('Are you sure you want to cancel this order?')) {
      try {
        await orderService.cancel(id);
        fetchOrder();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to cancel order');
      }
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  if (!order) {
    return <ErrorMessage message="Order not found" />;
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'processing';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Product',
      key: 'product',
      render: (_, record) => <Text>{record.product_name || 'N/A'}</Text>,
    },
    {
      title: 'Price',
      dataIndex: 'product_price',
      key: 'price',
      render: (price) => `${(price || 0).toLocaleString()} UZS`,
    },
    {
      title: 'Quantity',
      key: 'quantity',
      render: (_, record) => (
        editingItem === record.id ? (
          <Input
            value={editedQuantity}
            onChange={(e) => setEditedQuantity(e.target.value)}
            type="number"
            style={{ width: 80 }}
          />
        ) : (
          record.quantity
        )
      ),
    },
    {
      title: 'Subtotal',
      key: 'subtotal',
      render: (_, record) => {
        const qty = editingItem === record.id ? parseInt(editedQuantity, 10) || 0 : record.quantity;
        return `${((record.product_price || 0) * qty).toLocaleString()} UZS`;
      },
    },
  ];

  if (order.status === 'pending') {
    columns.push({
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {editingItem === record.id ? (
            <>
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={handleSaveItem}
              />
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={handleCancelEdit}
              />
            </>
          ) : (
            <Button
              type="primary"
              ghost
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditItem(record)}
            />
          )}
        </Space>
      ),
    });
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>Order #{order.order_number}</Title>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/seller/pending-orders')}>
            Back to Orders
          </Button>
          {order.status === 'pending' && (
            <>
              <Button
                type="primary"
                onClick={() => setOpenConfirmDialog(true)}
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              >
                Confirm Order
              </Button>
              <Button danger onClick={handleCancelOrder}>
                Cancel Order
              </Button>
            </>
          )}
        </Space>
      </div>

      <Row gutter={[24, 24]}>
        {/* Order Information */}
        <Col xs={24} md={12}>
          <Card title="Order Information">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Status:</Text>
                <Tag color={getStatusColor(order.status)} style={{ textTransform: 'capitalize' }}>
                  {order.status}
                </Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Order Date:</Text>
                <Text>
                  {new Date(order.createdAt).toLocaleString()}
                </Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Total Amount:</Text>
                <Text strong>
                  {(order.total_amount || 0).toLocaleString()} UZS
                </Text>
              </div>
            </div>
          </Card>
        </Col>

        {/* Customer Information */}
        <Col xs={24} md={12}>
          <Card title="Customer Information">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Name:</Text>
                <Text>
                  {order.customer
                    ? `${order.customer.first_name} ${order.customer.last_name || ''}`
                    : 'N/A'}
                </Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Phone:</Text>
                <Text>{order.customer?.phone_number || 'N/A'}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Address:</Text>
                <Text style={{ maxWidth: '60%', textAlign: 'right' }}>
                  {order.delivery_address}
                </Text>
              </div>
              {order.customer_note && (
                <div>
                  <Text type="secondary" style={{ display: 'block' }}>Notes:</Text>
                  <Text>{order.customer_note}</Text>
                </div>
              )}
            </div>
          </Card>
        </Col>

        {/* Order Items */}
        <Col xs={24}>
          <Card
            title="Order Items"
            extra={order.status === 'pending' && <Text type="secondary" style={{ fontSize: 12 }}>You can edit quantities before confirming</Text>}
          >
            <Table
              columns={columns}
              dataSource={order.orderItems}
              rowKey="id"
              pagination={false}
            />
          </Card>
        </Col>
      </Row>

      {/* Confirm Dialog */}
      <Modal
        title="Confirm Order"
        open={openConfirmDialog}
        onCancel={() => setOpenConfirmDialog(false)}
        onOk={handleConfirmOrder}
        okText="Confirm"
        okButtonProps={{ style: { backgroundColor: '#52c41a', borderColor: '#52c41a' } }}
      >
        <p>Are you sure you want to confirm this order? The customer will be notified.</p>
      </Modal>
    </div>
  );
};

export default OrderManager;
